/**
 * DD-ATTR-FINISHING-001 (F-088 + F-076) — the Hardware & Finishing values of an intake spec,
 * as attribute strings.
 *
 * The proof packet printed these straight from the frozen intake spec while the Attributes
 * panel never received them (the intake fill stopped at Laminate), so grommets or hardware
 * were "only in the packet" (1027-03, Justin's hardware line). This is the ONE place the spec
 * is turned into text: the intake fill seeds the attributes from it and the packet falls back
 * to it, so the panel and the packet always agree — and a value the PDS edits or clears in
 * Triage / Edit Line wins on both (F-057 class, attribute-first).
 */
import type { OriginSpec } from '@/types/origin'
import { joinMulti } from '@/lib/dd-queue/attributeMulti'

export const FINISHING_KEYS = [
  'taping',
  'rounded_corners',
  'mounting_holes',
  'grommets',
  'hem',
  'pole_pocket',
  'hardware'
] as const

export type FinishingKey = (typeof FINISHING_KEYS)[number]

/** Packet row labels — unchanged from the original packet ("Corners", not "Rounded Corners"). */
export const FINISHING_PACKET_LABELS: Record<FinishingKey, string> = {
  taping: 'Application Tape',
  rounded_corners: 'Corners',
  mounting_holes: 'Mounting Holes',
  grommets: 'Grommets',
  hem: 'Hem',
  pole_pocket: 'Pole Pocket',
  hardware: 'Hardware'
}

export const HARDWARE_REQUIRED_TEXT = 'Required — see notes'

/** White-ink option codes → the label the packet prints (moved here from packet.ts; re-exported there). */
export const WHITE_INK_LABELS: Record<string, string> = {
  flood: 'Flood White',
  spot: 'Spot White',
  fiveWS: '5WS (5-white spec)',
  threeWS: '3WS (3-white spec)'
}

/**
 * "Flood White; Spot White" — the White Ink attribute text; null when the spec has none.
 * DD-ATTR-OPTIONS-001: joined with the multi-value separator (White Ink is a multi-select
 * attribute now), so the fill is exactly what the picker stores.
 */
export function whiteInkAttributeText(spec: OriginSpec | null | undefined): string | null {
  const codes = Array.isArray(spec?.white_ink) ? spec.white_ink : []
  const labels = codes.map((c) => text(c)).filter((c): c is string => c != null)
  if (labels.length === 0) return null
  return joinMulti(labels.map((c) => WHITE_INK_LABELS[c] ?? c))
}

function text(v: unknown): string | null {
  const t = typeof v === 'string' ? v.trim() : ''
  return t === '' ? null : t
}

/** Sign Builder's "match hardware" diameter id — printed as words, not as "⌀ match-hardware\"". */
export const MATCH_HARDWARE_DIAMETER = 'match-hardware'

/**
 * "all 4 corners, ⌀ 0.375\"" — the one wording for mounting holes (attribute, packet, and the
 * option list in the attribute schema). Location ids keep their dashes turned into spaces;
 * DD-ATTR-OPTIONS-001: the "match hardware" diameter reads as words.
 */
export function formatMountingHoles(location: unknown, diameter: unknown): string {
  // CR-004: non-empty parts joined — a diameter without a location never starts with ", ".
  const parts: string[] = []
  const where = text(location)?.replace(/-/g, ' ')
  if (where) parts.push(where)
  const dia = text(diameter)
  if (dia) parts.push(dia === MATCH_HARDWARE_DIAMETER ? 'match hardware' : `⌀ ${dia}"`)
  return parts.join(', ')
}

/** Only the keys the spec actually carries, in packet order; values are the packet's wording. */
export function finishingFromSpec(
  spec: OriginSpec | null | undefined
): Partial<Record<FinishingKey, string>> {
  const out: Partial<Record<FinishingKey, string>> = {}
  if (!spec) return out

  const taping = text(spec.taping)
  if (taping) out.taping = taping

  const corners = text(spec.rounded_corners)
  if (corners) out.rounded_corners = corners

  if (spec.mounting_holes) {
    const value = formatMountingHoles(spec.mounting_holes.location, spec.mounting_holes.diameter)
    if (value) out.mounting_holes = value
  }

  const grommets = text(spec.grommets)
  if (grommets) out.grommets = grommets

  const hem = text(spec.hem)
  if (hem) out.hem = hem

  // Wire type is boolean; older fixtures / payloads carry the label ("Yes").
  const pocket: unknown = spec.pole_pocket
  if (pocket === true) out.pole_pocket = 'Yes'
  else if (typeof pocket === 'string' && text(pocket)) out.pole_pocket = text(pocket) as string

  if (spec.hardware_needed) {
    out.hardware = text(spec.hardware_description) ?? HARDWARE_REQUIRED_TEXT
  }

  return out
}
